import { useEffect } from 'react';
import {
  Box, Button, Flex, HStack, Heading, Skeleton, Text, useDisclosure, useToast, VStack,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PushToggle } from '../push/PushToggle';
import { InviteCard } from '../referrals/InviteCard';
import { useGetCreditsBalanceQuery } from '../redux/services/mixgenie';
import { PaywallModal } from '../paywall/PaywallModal';
import { MG_GRADIENT } from '../theme';

/**
 * /credits — balance summary + a single "Get more credits" button that
 * opens the shared PaywallModal (same component the composer / studio /
 * header credits chip all use). Pre-cutover this page had its own
 * dark-theme inline pack / plan grid behind a BILLING_ENABLED feature
 * flag with a "coming soon" placeholder when the flag was off — all
 * stale now that the paywall ships and billing is live. Everything
 * related to actual purchase decisions lives in the modal.
 */
export default function CreditsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const paywall = useDisclosure();

  const { data: creditsData, isLoading: balanceLoading, refetch: refetchBalance } = useGetCreditsBalanceQuery();
  const balance = (creditsData as any)?.data || { creditsRemaining: 0, plan: 'starter', currentPlan: 0, purchased: 0, status: null };
  const hasActiveSub = balance.status === 'active';
  const planName = balance.plan === 'starter' ? 'MixGenie Free Signup' : balance.plan;

  // Stripe return — same handling as the pre-cutover page: toast + refetch
  // on success, info toast on cancel.
  useEffect(() => {
    const checkout = searchParams.get('checkout') || searchParams.get('billing');
    if (!checkout) return;
    if (checkout === 'success') {
      toast({
        title: t('credits.checkoutSuccess', 'Payment received — your credits are updating.'),
        status: 'success', duration: 4000, isClosable: true,
      });
      refetchBalance();
    } else if (checkout === 'cancelled' || checkout === 'cancel') {
      toast({
        title: t('credits.checkoutCancelled', 'Checkout cancelled.'),
        status: 'info', duration: 3000, isClosable: true,
      });
    }
    searchParams.delete('checkout');
    searchParams.delete('billing');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, toast, refetchBalance, t]);

  return (
    <Box color="mg.fg">
      <Heading size="lg" mb={6}>Credits</Heading>

      {/* Notifications row — kept from the previous page; the only
          non-billing control that lived here. */}
      <Box
        bg="mg.surface"
        border="1px solid"
        borderColor="mg.line"
        borderRadius="14px"
        p={4}
        mb={5}
      >
        <PushToggle />
      </Box>

      {/* Balance summary — big number + plan + per-bucket breakdown. */}
      <Box
        bg="mg.surface"
        border="1px solid"
        borderColor="mg.line"
        borderRadius="14px"
        p={6}
        mb={5}
      >
        <Text fontSize="13px" color="mg.muted" fontWeight={600} letterSpacing="0.04em" textTransform="uppercase" mb={2}>
          Credits remaining
        </Text>
        {balanceLoading ? (
          <Skeleton height="40px" width="120px" />
        ) : (
          <HStack align="baseline" spacing={3} mb={2}>
            <Text fontSize="48px" fontWeight={800} lineHeight={1}>
              {balance.creditsRemaining ?? 0}
            </Text>
            {planName && (
              <Box
                px={3}
                py={1}
                borderRadius="full"
                bg="mg.surfaceHi"
                border="1px solid"
                borderColor="mg.line"
                fontSize="12px"
                fontWeight={700}
                color="mg.muted"
              >
                {planName}
              </Box>
            )}
          </HStack>
        )}
        <Text fontSize="13px" color="mg.muted">
          Plan: {balance.currentPlan ?? 0} · Purchased: {balance.purchased ?? 0}
          {hasActiveSub && balance.renewalDate && (
            <> · Renews {new Date(balance.renewalDate).toLocaleDateString()}</>
          )}
        </Text>

        <Flex mt={5} gap={2} direction={{ base: 'column', sm: 'row' }}>
          <Button
            onClick={paywall.onOpen}
            size="lg"
            bg="hot.500"
            color="white"
            _hover={{ bg: 'hot.600' }}
            borderRadius="12px"
            px={6}
            bgGradient={MG_GRADIENT}
            flex={{ base: '1', sm: '0 1 auto' }}
          >
            Get more credits
          </Button>
          <Button
            onClick={() => refetchBalance()}
            size="lg"
            variant="outline"
            borderColor="mg.line2"
            color="mg.fg"
            _hover={{ bg: 'mg.surfaceHi' }}
            borderRadius="12px"
            flex={{ base: '1', sm: '0 1 auto' }}
          >
            Refresh
          </Button>
        </Flex>
      </Box>

      {/* Earn credits — invite friends. Keeping the referral card from
          the previous page since it's useful here and nowhere else. */}
      <Box
        bg="mg.surface"
        border="1px solid"
        borderColor="mg.line"
        borderRadius="14px"
        p={6}
        mb={5}
      >
        <VStack align="stretch" spacing={3}>
          <Text fontSize="18px" fontWeight={700}>Earn free credits</Text>
          <Text fontSize="14px" color="mg.muted">
            Invite friends — you both get credits when they sign up and create their first song.
          </Text>
          <InviteCard />
        </VStack>
      </Box>

      <PaywallModal
        isOpen={paywall.isOpen}
        onClose={paywall.onClose}
        reason="open"
      />
    </Box>
  );
}
